import { lazy, Suspense, useEffect } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { AppProvider } from "./contexts/AppContext";
import { UserProvider, useUser } from "./contexts/UserContext";

import AppLayout from "./AppLayout.jsx";
import LoadingOverlay from "./components/LoadingOverlay.jsx";
import PrinterConnectionModal from "./components/PrinterConnectionModal.jsx";
import LoginPage from "./pages/login/LoginPage.jsx";
import PatientManagement from "./pages/patient_management/PatientManagement.jsx";
import FloatingRecorder from "./pages/visting_management/presscribe/FloatingRecorder.jsx";
import { RecorderProvider, useRecorder } from "./pages/visting_management/presscribe/RecorderContext.jsx";
import { FloatingTelemedOverlay, TelemedOverlayProvider } from "./pages/telemed/TelemedVideoCall";
import SummarySettingsModal from "./pages/visting_management/summary/SummarySettingsModal.jsx";
import realtimeClient from "./realtime/client";
import {
  handleMedicineStickerEvent,
  handleOpenCashDrawerEvent,
  handlePatientStickerEvent,
  handleQueueTicketEvent,
} from "./realtime/subscriptions";
import { useIsMobileClinicUnsupportedViewport } from "./utils/device";
import { isLiffRoutePath } from "./utils/liffRoute";


const AppointmentManagement = lazy(() => import("./pages/appointment_management/AppointmentManagement.jsx"));
const FollowUpListPage = lazy(() => import("./pages/follow_up/FollowUpListPage"));
const InternalTools = lazy(() => import("./pages/internal_tools/InternalTools.jsx"));
const AccountRoutes = lazy(() => import("./pages/login/AccountRoutes.jsx"));
const UnsupportedDevicePage = lazy(() => import("./pages/login/UnsupportedDevicePage.jsx"));
const PatientProfile = lazy(() => import("./pages/patient_management/PatientProfile.jsx"));
const LabResultsPage = lazy(() => import("./pages/patient_management/LabResultsPage"));
const PaymentDetailPage = lazy(() => import("./pages/payment/PaymentDetailPage"));
const PaymentOverviewPage = lazy(() => import("./pages/payment/PaymentOverviewPage"));
const PrintReceiptPage = lazy(() => import("./pages/payment/PrintReceiptPage.jsx"));
const PharmacyDispenseManagement = lazy(() => import("./pages/pharmacy_dispense_management/PharmacyDispenseManagement.jsx"));
const QueueManagement = lazy(() => import("./pages/queue_management/QueueManagement.jsx"));
const SettingsPage = lazy(() => import("./pages/settings/SettingsPage.jsx"));
const PrintLabResultsPage = lazy(() => import("./pages/PrintLabResultsPage"));
const LabResultsListPage = lazy(() => import("./pages/lab/LabResultsListPage"));
const ClinicStock = lazy(() => import("./pages/stock_and_inventory_management/ClinicStock.jsx"));
const DailyStockCheckPage = lazy(() => import("./pages/stock_and_inventory_management/DailyStockCheckPage.jsx"));
const MedicalCertificateTemplate = lazy(() => import("./pages/stock_and_inventory_management/MedicalCertificateTemplate.jsx"));
const UIExample = lazy(() => import("./pages/UIExample.jsx"));
const VisitingManagement = lazy(() => import("./pages/visting_management/VisitingManagement.jsx"));
const MobileVisitingManagement = lazy(() => import("./pages/visting_management/MobileVisitingManagement.jsx"));
const TreatmentFormExamplePage = lazy(() => import("./pages/line_oa/feedback/treatment_form_example.jsx"));
const LiffApp = lazy(() => import("./pages/line_oa/LiffApp.jsx"));
const TelemedClientJoinPage = lazy(() => import("./pages/telemed/TelemedClientJoinPage"));

const APP_HOME_PATH = "/";
const UNSUPPORTED_DEVICE_PATH = "/app/upsupported-device";
const VITE_PRELOAD_RELOAD_KEY_PREFIX = "vite-preload-reload:";

const EXACT_ROUTES_WITHOUT_CLINIC_SERVICES = new Set([
  "/account/login",
  "/account/password-reset",
  "/account/md-card-input",
  "/account/doctor-file-upload",
  "/account/staff-register-success",
  "/account/doctor-e-signature",
  "/account/branch-selection",
  "/feedback-form",
  UNSUPPORTED_DEVICE_PATH,
]);

const EXACT_ROUTES_WITHOUT_PRINTER_CONNECTION_MODAL = new Set([
  "/mobile/visiting-management",
]);

function isRouteWithoutClinicServices(pathname = "") {
  return EXACT_ROUTES_WITHOUT_CLINIC_SERVICES.has(pathname)
    || isTelemed(pathname)
    || isLiffRoutePath(pathname);
}

function isTelemed(pathname){
  return pathname.includes("/telemed/join/");
}

function isRouteWithoutPrinterConnectionModal(pathname = "") {
  return isRouteWithoutClinicServices(pathname)
    || EXACT_ROUTES_WITHOUT_PRINTER_CONNECTION_MODAL.has(pathname);
}

function RouteLoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingOverlay />
    </div>
  );
}

export function VitePreloadErrorRecovery({
  reload = () => window.location.reload(),
  storage = window.sessionStorage,
}) {
  useEffect(() => {
    function handlePreloadError(event) {
      const reloadKey = `${VITE_PRELOAD_RELOAD_KEY_PREFIX}${window.location.pathname}:${String(event.payload ?? "")}`;
      try {
        if (storage.getItem(reloadKey)) {
          return;
        }
        storage.setItem(reloadKey, "true");
      } catch {
        // Reloading is still preferable to leaving the stale lazy route unusable.
      }

      event.preventDefault();
      reload();
    }

    window.addEventListener("vite:preloadError", handlePreloadError);
    return () => window.removeEventListener("vite:preloadError", handlePreloadError);
  }, [reload, storage]);

  return null;
}

function DesktopSupportedRoute() {
  const isUnsupportedViewport = useIsMobileClinicUnsupportedViewport();
  const { user } = useUser() ?? {};

  if (isUnsupportedViewport) {
    if (user?.role === "แพทย์") {
      return <Navigate to="/mobile/visiting-management" replace />;
    }
    return <Navigate to={UNSUPPORTED_DEVICE_PATH} replace />;
  }

  return <Outlet />;
}

function RecorderModalLayer() {
  const {
    activeTreatmentId,
    clinicalNoteSettings,
    closeSummarySettingsModal,
    isSummarySettingsModalEditMode,
    isSummarySettingsModalOpen,
  } = useRecorder();

  return (
    <SummarySettingsModal
      isOpen={isSummarySettingsModalOpen}
      onClose={closeSummarySettingsModal}
      editMode={isSummarySettingsModalEditMode}
      initialValues={clinicalNoteSettings || undefined}
      treatmentId={activeTreatmentId}
    />
  );
}

function RouteAwareRealtimeSubscriptions() {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname ?? "";
    if (isRouteWithoutClinicServices(pathname)) {
      realtimeClient.disconnect();
      return undefined;
    }

    const queuePrintSubscriptionOptions = { emitCachedPayload: false };
    const unsubscribeNewQueue = realtimeClient.subscribe(
      "new_queue",
      handleQueueTicketEvent,
      queuePrintSubscriptionOptions,
    );
    const unsubscribeReprintQueue = realtimeClient.subscribe(
      "re_print_queue",
      handleQueueTicketEvent,
      queuePrintSubscriptionOptions,
    );
    const unsubscribeOpenCashDrawer = realtimeClient.subscribe(
      "open_cash_drawer",
      handleOpenCashDrawerEvent,
      { emitCachedPayload: false },
    );
    const unsubscribePatientStickerPrint = realtimeClient.subscribe(
      "patient_sticker_print",
      handlePatientStickerEvent,
      queuePrintSubscriptionOptions,
    );
    const unsubscribeMedicineStickerPrint = realtimeClient.subscribe(
      "medicine_sticker_print",
      handleMedicineStickerEvent,
      queuePrintSubscriptionOptions,
    );

    return () => {
      unsubscribeNewQueue();
      unsubscribeReprintQueue();
      unsubscribeOpenCashDrawer();
      unsubscribePatientStickerPrint();
      unsubscribeMedicineStickerPrint();
    };
  }, [location.pathname]);

  return null;
}

function RouteAwarePrinterConnectionModal() {
  const location = useLocation();
  const pathname = location.pathname ?? "";

  if (isRouteWithoutPrinterConnectionModal(pathname)) {
    return null;
  }

  return <PrinterConnectionModal />;
}

function RouteAwareFloatingRecorder() {
  const location = useLocation();
  const { user } = useUser() ?? {};
  const pathname = location.pathname ?? "";

  if (!user || isRouteWithoutClinicServices(pathname)) {
    return null;
  }

  return <FloatingRecorder />;
}

function RouteAwareFloatingTelemedOverlay() {
  const location = useLocation();
  const { user } = useUser() ?? {};
  const pathname = location.pathname ?? "";

  if (!user || isRouteWithoutClinicServices(pathname)) {
    return null;
  }

  return <FloatingTelemedOverlay shouldDockAbovePresscribe={!pathname.startsWith("/visiting-management")} />;
}

function RouteAwareRecorderLifecycle() {
  const location = useLocation();
  const { loading, user } = useUser() ?? {};
  const {
    closeSummarySettingsModal,
    resetRecorder,
  } = useRecorder();
  const pathname = location.pathname ?? "";
  const shouldResetRecorder = !loading && (
    !user
    || isRouteWithoutClinicServices(pathname)
  );

  useEffect(() => {
    if (!shouldResetRecorder) {
      return;
    }

    closeSummarySettingsModal();
    void resetRecorder();
  }, [closeSummarySettingsModal, resetRecorder, shouldResetRecorder]);

  return null;
}

export default function App() {
  return (
    <AppProvider>
      <UserProvider>
        <RecorderProvider>
          <TelemedOverlayProvider>
            <VitePreloadErrorRecovery />
            <RouteAwareRealtimeSubscriptions />
            <RouteAwareRecorderLifecycle />
            <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
              {/* Routes with Navigation layout */}
              <Route element={<DesktopSupportedRoute />}>
                <Route element={<AppLayout />}>
                  <Route path="/" element={<PatientManagement />} />
                  <Route path="/patient-management" element={<PatientManagement />} />
                  <Route path="/queue-management" element={<QueueManagement />} />
                  <Route path="/visiting-management" element={<VisitingManagement />} />
                  <Route path="/visiting-management/:treatmentId" element={<VisitingManagement />} />
                  <Route path="/pharmacy-dispense-management" element={<PharmacyDispenseManagement />} />
                  <Route path="/appointment-management" element={<AppointmentManagement />} />
                  <Route path="/follow-up-management" element={<FollowUpListPage />} />
                  <Route path="/payments" element={<PaymentOverviewPage />} />
                  <Route path="/payment-details/:paymentId" element={<PaymentDetailPage />} />
                  <Route path="/payments/print-receipt" element={<PrintReceiptPage />} />
                  <Route path="/patient-management/profile/:patientId" element={<PatientProfile />} />
                  <Route path="/patient-management/lab-results" element={<LabResultsPage />} />
                  <Route path="/inventory" element={<ClinicStock />} />
                  <Route path="/inventory/daily-stock-check" element={<DailyStockCheckPage />} />
                  <Route path="/medical-certificate-template" element={<MedicalCertificateTemplate />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/print-lab-result" element={<PrintLabResultsPage />} />
                  <Route path="/lab-results-list/" element={<LabResultsListPage />} />
                </Route>
                {import.meta.env.VITE_ENABLE_INTERNAL_TOOLS?.toLowerCase() === 'true' && (
                  <Route path="/internal-tools" element={<InternalTools />} />
                )}
              </Route>
              {/* Routes rendered without the Navigation layout */}
              <Route path="/account/login" element={<LoginPage />} />
              <Route path="/account/*" element={<AccountRoutes />} />
              <Route path={UNSUPPORTED_DEVICE_PATH} element={<UnsupportedDevicePage />} />
              <Route path="/mobile/visiting-management" element={<MobileVisitingManagement />} />
              <Route path="/feedback-form" element={<TreatmentFormExamplePage />} />
              <Route path="/liff/*" element={<LiffApp />} />
              <Route path="/telemed/join/:meetingId" element={<TelemedClientJoinPage />} />
              <Route path="/ui-example" element={<UIExample />} />
              <Route path="*" element={<Navigate to={APP_HOME_PATH} replace />} />
            </Routes>
            </Suspense>
            <RouteAwareFloatingRecorder />
            <RouteAwareFloatingTelemedOverlay />
            <RecorderModalLayer />
            <RouteAwarePrinterConnectionModal />
          </TelemedOverlayProvider>
        </RecorderProvider>
      </UserProvider>
    </AppProvider>
  );
}
